// WebSocket Data Manager - Single Source of Truth Pattern
// This manager provides a centralized way to handle WebSocket data
// and transform it for different component needs.

class WebSocketDataManager {
  constructor(socket) {
    this.socket = socket;
    this.subscribers = new Map();
    this.data = {
      baseFeedData: {},
      companyPriceData: {},
      lastUpdate: null,
    };

    this.setupSocketListeners();
  }

  setupSocketListeners() {
    // Listen to base events
    this.socket.on('latest-feed-history', (response) => {
      this.data.baseFeedData = response.data;
      this.data.lastUpdate = new Date();
      this.notifySubscribers('baseFeedData', response.data);
    });

    this.socket.on('latest-tomei-price', (response) => {
      // Store the new data
      const newData = response.data;

      // Only update if we actually received data
      if (newData && typeof newData === 'object') {
        this.data.companyPriceData = newData;
        this.data.lastUpdate = new Date();
        this.notifySubscribers('companyPriceData', newData);
      }
    });

    // Dedicated listener for CurrentPriceInfo component
    this.socket.on('current-price-info-data', (response) => {
      const { baseFeedData, companyPriceData } = response.data || {};

      if (baseFeedData) {
        this.data.baseFeedData = baseFeedData;
        this.notifySubscribers('currentPriceInfoBaseFeed', baseFeedData);
      }

      if (companyPriceData) {
        // Store company price data so it can be accessed by getCurrentPriceInfoPriceData()
        this.data.companyPriceData = {
          ...this.data.companyPriceData,
          ...companyPriceData,
        };
        this.notifySubscribers('currentPriceInfoCompanyData', companyPriceData);
      }

      this.data.lastUpdate = new Date();
    });
  }

  // Subscription management
  subscribe(dataType, callback) {
    if (!this.subscribers.has(dataType)) {
      this.subscribers.set(dataType, new Set());
    }
    this.subscribers.get(dataType).add(callback);

    // Return unsubscribe function
    return () => {
      this.subscribers.get(dataType)?.delete(callback);
    };
  }

  notifySubscribers(dataType, data) {
    this.subscribers.get(dataType)?.forEach((callback) => {
      try {
        callback(data);
      } catch (error) {
        console.error('Error in subscriber callback:', error);
      }
    });
  }

  // Data transformation methods for different components

  // Get data formatted for Tomei Price Override Table (company-specific)
  getTomeiPriceOverrideData(selectedCompany) {
    const { companyPriceData } = this.data;

    if (!selectedCompany || !companyPriceData) {
      return [];
    }

    // ONLY return data for the exact selected company - no fallbacks
    // This prevents flickering when other components request different company data
    if (companyPriceData[selectedCompany]) {
      const companyData = companyPriceData[selectedCompany];
      if (companyData.Feeds) {
        return this.formatCompanyFeedData(companyData);
      }
    }

    // No fallback for manual override - each company should show only its own data
    return [];
  }

  formatCompanyFeedData(companyData) {
    const formattedData = [];
    Object.keys(companyData.Feeds).forEach((feedName) => {
      const feedData = companyData.Feeds[feedName];
      const d = {
        key: feedName,
        FeedName: feedData.FeedName,
        FeedBuyPrice:
          feedData.FeedBuyPrice == 0 && feedData.IsManualPriceActivatedYN == 'N'
            ? 'N/A'
            : feedData.TomeiBuyPrice,
        FeedSellPrice:
          feedData.FeedSellPrice == 0 &&
          feedData.IsManualPriceActivatedYN == 'N'
            ? 'N/A'
            : feedData.TomeiSellPrice,
        IsManualPriceActivatedYN: feedData.IsManualPriceActivatedYN,
      };

      // This is to ensure when the price is updated, the manual price that user type will not be overwritten
      if (d.IsManualPriceActivatedYN === 'Y') {
        d.ManualBuyPrice = d.FeedBuyPrice;
        d.ManualSellPrice = d.FeedSellPrice;
      }
      formattedData.push(d);
    });

    return formattedData;
  }

  // Get data formatted for first available company (fallback)
  getFirstAvailableCompanyData() {
    const { companyPriceData } = this.data;

    if (!companyPriceData || Object.keys(companyPriceData).length === 0) {
      return [];
    }

    const firstCompanyCode = Object.keys(companyPriceData)[0];
    return this.getTomeiPriceOverrideData(firstCompanyCode);
  }

  // Get data formatted for Price Overview
  getPriceOverviewData() {
    const { baseFeedData, companyPriceData } = this.data;

    return {
      rawFeeds: baseFeedData,
      processedPrices: companyPriceData,
      summary: this.calculatePriceSummary(),
    };
  }

  // Get data formatted for Feed Price Override Table
  getFeedPriceOverrideData() {
    const { baseFeedData } = this.data;

    if (!baseFeedData || Object.keys(baseFeedData).length === 0) {
      return [];
    }

    const formattedData = [];
    Object.keys(baseFeedData).forEach((key) => {
      const feedData = baseFeedData[key];
      const d = {
        key: key,
        FeedName: feedData.FeedName,
        FeedBuyPrice:
          feedData.FeedBuyPrice == 0 && feedData.IsManualPriceActivatedYN == 'N'
            ? 'N/A'
            : feedData.FeedBuyPrice,
        FeedSellPrice:
          feedData.FeedSellPrice == 0 &&
          feedData.IsManualPriceActivatedYN == 'N'
            ? 'N/A'
            : feedData.FeedSellPrice,
        IsManualPriceActivatedYN: feedData.IsManualPriceActivatedYN,
      };

      // This is to ensure when the price is updated, the manual price that user type will not be overwritten
      if (d.IsManualPriceActivatedYN === 'Y') {
        d.ManualBuyPrice = d.FeedBuyPrice;
        d.ManualSellPrice = d.FeedSellPrice;
      }
      formattedData.push(d);
    });

    return formattedData;
  }

  // Get latest feed prices (replaces direct latest-feed-history usage)
  getLatestFeedPrices(feedNames = ['XAU/MYR']) {
    const { baseFeedData } = this.data;

    return feedNames.reduce((result, feedName) => {
      if (baseFeedData[feedName]) {
        result[feedName] = baseFeedData[feedName];
      }
      return result;
    }, {});
  }

  // Get data formatted for Dashboard Feed Table
  getDashboardFeedData() {
    const { baseFeedData } = this.data;

    if (!baseFeedData || Object.keys(baseFeedData).length === 0) {
      return [];
    }

    // Only GOLD - 1g feed is supported
    const feedMap = [
      {
        displayName: 'GOLD - 1g',
        sourceFeed: 'XAU/MYR',
        multiplier: 1,
      },
    ];

    const formattedData = feedMap.map((feedConfig) => {
      const sourceData = baseFeedData[feedConfig.sourceFeed];

      if (!sourceData) {
        return {
          feed: feedConfig.displayName,
          weBuy: 'N/A',
          weSell: 'N/A',
          lastUpdate: 'N/A',
        };
      }

      const weBuy = sourceData.FeedBuyPrice
        ? (sourceData.FeedBuyPrice * feedConfig.multiplier).toFixed(2)
        : 'N/A';
      const weSell = sourceData.FeedSellPrice
        ? (sourceData.FeedSellPrice * feedConfig.multiplier).toFixed(2)
        : 'N/A';

      return {
        feed: feedConfig.displayName,
        weBuy: weBuy,
        weSell: weSell,
        lastUpdate: sourceData.DateTime || 'N/A',
        // Include cut-off status information
        IsManualCutOffYN: sourceData?.IsManualCutOffYN,
        IsCircuitBreakerActivatedYN: sourceData?.IsCircuitBreakerActivatedYN,
        // Check if feed is cut off (either manually or by circuit breaker)
        IsCutOff:
          sourceData?.IsManualCutOffYN === 'Y' ||
          sourceData?.IsCircuitBreakerActivatedYN === 'Y',
      };
    });

    return formattedData;
  }

  // Get data formatted for Dashboard Price Data (company-specific)
  getDashboardPriceData() {
    const { companyPriceData, baseFeedData } = this.data;

    if (!companyPriceData || Object.keys(companyPriceData).length === 0) {
      return {};
    }

    // Get the first available company's data (prioritize 'CTG' if available)
    let companyData = null;
    if (companyPriceData['CTG']) {
      companyData = companyPriceData['CTG'];
    } else {
      const firstCompanyCode = Object.keys(companyPriceData)[0];
      if (firstCompanyCode) {
        companyData = companyPriceData[firstCompanyCode];
      }
    }

    let finalData = {};
    if (companyData && companyData.Feeds) {
      // Process the feeds data
      Object.keys(companyData.Feeds).forEach((feedName) => {
        const feedData = companyData.Feeds[feedName];

        // Use the actual feed name from the data
        let displayName = feedData.FeedName || feedName;

        // Determine the base feed name to check cut-off status
        let baseFeedName = null;
        if (displayName.includes('GOLD')) {
          baseFeedName = 'XAU/MYR';
        } else if (displayName.includes('SILVER')) {
          baseFeedName = 'XAG/MYR';
        }

        // Get cut-off status from base feed data
        let isCutOff = false;
        let isManualCutOffYN = 'N';
        let isCircuitBreakerActivatedYN = 'N';

        if (baseFeedName && baseFeedData && baseFeedData[baseFeedName]) {
          const baseFeed = baseFeedData[baseFeedName];
          isManualCutOffYN = baseFeed.IsManualCutOffYN || 'N';
          isCircuitBreakerActivatedYN =
            baseFeed.IsCircuitBreakerActivatedYN || 'N';
          isCutOff =
            isManualCutOffYN === 'Y' || isCircuitBreakerActivatedYN === 'Y';
        }

        finalData[displayName] = {
          WeBuy: feedData?.TomeiBuyPrice,
          WeSell: feedData?.TomeiSellPrice,
          // Include cut-off status information from base feed data
          IsManualCutOffYN: isManualCutOffYN,
          IsCircuitBreakerActivatedYN: isCircuitBreakerActivatedYN,
          // Check if feed is cut off (either manually or by circuit breaker)
          IsCutOff: isCutOff,
        };
      });
    }

    return finalData;
  }

  // Get data formatted for CurrentPriceInfo component (feed data)
  getCurrentPriceInfoFeedData() {
    const { baseFeedData } = this.data;

    const defaultData = {
      'XAU/MYR': {
        FeedBuyPrice: 0,
        FeedSellPrice: 0,
      },
    };

    if (!baseFeedData || Object.keys(baseFeedData).length === 0) {
      return defaultData;
    }

    let formattedData = {};
    Object.keys(baseFeedData).forEach((key) => {
      const d = {
        key: key,
        FeedName: baseFeedData[key].FeedName,
        FeedBuyPrice: baseFeedData[key].SourceFeedBuyPrice,
        FeedSellPrice: baseFeedData[key].SourceFeedSellPrice,
        IsManualPriceActivatedYN: baseFeedData[key].IsManualPriceActivatedYN,
        IsSourceFeedAvailableYN: baseFeedData[key].IsSourceFeedAvailableYN,
        IsCutOffYN: baseFeedData[key].IsManualCutOffYN,
        IsCircuitBreakerActivatedYN:
          baseFeedData[key].IsCircuitBreakerActivatedYN,
      };
      formattedData[key] = d;
    });

    // Ensure XAU/MYR is always present (removed XAG/MYR as only gold is supported)
    if (!formattedData['XAU/MYR']) {
      formattedData['XAU/MYR'] = defaultData['XAU/MYR'];
    }

    return formattedData;
  }

  // Get data formatted for CurrentPriceInfo component (price data)
  getCurrentPriceInfoPriceData() {
    const { companyPriceData, baseFeedData } = this.data;

    if (!companyPriceData || Object.keys(companyPriceData).length === 0) {
      return {};
    }

    // Get the first available company's data (prioritize 'CTG' if available)
    let companyData = null;
    if (companyPriceData['CTG']) {
      companyData = companyPriceData['CTG'];
    } else {
      const firstCompanyCode = Object.keys(companyPriceData)[0];
      if (firstCompanyCode) {
        companyData = companyPriceData[firstCompanyCode];
      }
    }

    if (!companyData || !companyData.Feeds) {
      return {};
    }

    let finalData = {};
    // Process the feeds data
    Object.keys(companyData.Feeds).forEach((feedName) => {
      const feedData = companyData.Feeds[feedName];

      // Use the actual feed name from the data
      let displayName = feedData.FeedName || feedName;

      // Determine the base feed name to check cut-off status
      let baseFeedName = null;
      if (displayName.includes('GOLD')) {
        baseFeedName = 'XAU/MYR';
      } else if (displayName.includes('SILVER')) {
        baseFeedName = 'XAG/MYR';
      }

      // Get cut-off status from base feed data
      let isCutOff = false;
      let isManualCutOffYN = 'N';
      let isCircuitBreakerActivatedYN = 'N';

      if (baseFeedName && baseFeedData && baseFeedData[baseFeedName]) {
        const baseFeed = baseFeedData[baseFeedName];
        isManualCutOffYN = baseFeed.IsManualCutOffYN || 'N';
        isCircuitBreakerActivatedYN =
          baseFeed.IsCircuitBreakerActivatedYN || 'N';
        isCutOff =
          isManualCutOffYN === 'Y' || isCircuitBreakerActivatedYN === 'Y';
      }

      finalData[displayName] = {
        WeBuy: feedData?.TomeiBuyPrice,
        WeSell: feedData?.TomeiSellPrice,
        // Include cut-off status information from base feed data
        IsManualCutOffYN: isManualCutOffYN,
        IsCircuitBreakerActivatedYN: isCircuitBreakerActivatedYN,
        // Check if feed is cut off (either manually or by circuit breaker)
        IsCutOff: isCutOff,
      };
    });

    return finalData;
  }

  calculatePriceSummary() {
    // Add summary calculations here
    return {
      totalFeeds: Object.keys(this.data.baseFeedData).length,
      totalCompanies: Object.keys(this.data.companyPriceData).length,
      lastUpdate: this.data.lastUpdate,
    };
  }

  // Request methods (replace direct socket.emit calls)

  requestFeedData(feedNames = ['XAU/MYR']) {
    this.socket.emit('latest-feed-history');
  }

  requestCompanyPriceData(companyCodes) {
    this.socket.emit('latest-tomei-price', {
      companyCodes,
      event: 'latest-tomei-price',
    });
  }

  // Dedicated request method for CurrentPriceInfo component
  requestCurrentPriceInfoData() {
    this.socket.emit('current-price-info-data');
  }

  requestHistoricalData(params) {
    this.socket.emit('tomei-price-histories', {
      data: params,
      event: 'tomei-price-histories',
    });
  }
}

export default WebSocketDataManager;
